/**
 * useEntityName — Resolve any WSID to a display name with caching.
 *
 * Uses parseWSID() from the module registry to determine the module,
 * then queries coreBOS for the name field.
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { parseWSID } from "@/modules/entities/registry";

/** Module → name field mapping */
const NAME_FIELDS: Record<string, string> = {
  Accounts: "accountname",
  Contacts: "firstname, lastname",
  Documents: "notes_title",
  cbCalendar: "subject",
  evPolicies: "title",
  ProcessingActivities: "subject",
  Threats: "threatname",
  ThreatsMeasures: "subject",
  Audit: "subject",
  evAcceptance: "acctitle",
  evDataFlow: "subject",
  cbContent: "title",
  evAntiMoneyLaundering: "subject",
  ModComments: "commentcontent",
};

/** In-memory cache across all hook instances */
const nameCache = new Map<string, string>();

/** Batch-resolve multiple WSIDs grouped by module */
async function resolveNames(wsids: string[]): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  const toResolve = wsids.filter((id) => !nameCache.has(id));

  if (toResolve.length === 0) {
    wsids.forEach((id) => result.set(id, nameCache.get(id)!));
    return result;
  }

  // Group by module
  const groups = new Map<string, { module: string; ids: string[] }>();
  for (const wsid of toResolve) {
    const parsed = parseWSID(wsid);
    if (!parsed) continue;
    const mod = parsed.module.coreBOSModule;
    if (!groups.has(mod)) groups.set(mod, { module: mod, ids: [] });
    groups.get(mod)!.ids.push(wsid);
  }

  // Query each module group
  for (const [mod, group] of groups) {
    const nameField = NAME_FIELDS[mod];
    if (!nameField) continue;

    const idList = group.ids.map((id) => `'${id}'`).join(", ");
    try {
      const res = await axios.post("/api/direct/query", {
        query: `SELECT id, ${nameField} FROM ${mod} WHERE id IN (${idList}) LIMIT ${group.ids.length};`,
      });
      if (res.data.success) {
        for (const row of res.data.result || []) {
          let name: string;
          if (mod === "Contacts") {
            name = `${row.firstname || ""} ${row.lastname || ""}`.trim();
          } else {
            // Use whichever name field has a value
            name =
              row.accountname ||
              row.notes_title ||
              row.subject ||
              row.title ||
              row.threatname ||
              row.acctitle ||
              row.commentcontent ||
              row.id;
          }
          nameCache.set(row.id, name);
          result.set(row.id, name);
        }
      }
    } catch {
      // Fallback: use WSID as name
      group.ids.forEach((id) => {
        if (!result.has(id)) result.set(id, id);
      });
    }
  }

  // Fill from cache for already-resolved
  wsids.forEach((id) => {
    if (!result.has(id) && nameCache.has(id)) {
      result.set(id, nameCache.get(id)!);
    }
  });

  return result;
}

/** Resolve a single WSID to a display name */
export function useEntityName(wsid: string | undefined) {
  const [name, setName] = useState<string | undefined>(
    wsid ? nameCache.get(wsid) : undefined
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!wsid) {
      setName(undefined);
      return;
    }

    if (nameCache.has(wsid)) {
      setName(nameCache.get(wsid));
      return;
    }

    setLoading(true);
    resolveNames([wsid]).then((map) => {
      setName(map.get(wsid) || wsid);
      setLoading(false);
    });
  }, [wsid]);

  return { name, loading };
}

/** Batch resolve multiple WSIDs. Returns a Map<wsid, name>. */
export function useEntityNames(wsids: string[]) {
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (wsids.length === 0) return;

    // Check if all already cached
    const allCached = wsids.every((id) => nameCache.has(id));
    if (allCached) {
      const map = new Map<string, string>();
      wsids.forEach((id) => map.set(id, nameCache.get(id)!));
      setNames(map);
      return;
    }

    setLoading(true);
    resolveNames(wsids).then((map) => {
      setNames(map);
      setLoading(false);
    });
  }, [wsids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  return { names, loading };
}

/** Get module info for a WSID (type label, icon, route) */
export function getEntityInfo(wsid: string) {
  const parsed = parseWSID(wsid);
  if (!parsed) return null;
  return {
    moduleName: parsed.module.name,
    label: parsed.module.label,
    icon: parsed.module.navigation.icon,
    coreBOSModule: parsed.module.coreBOSModule,
    route: `/${parsed.module.name}/${wsid}`,
  };
}
